import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Navbar, Footer } from '../components/Navbar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import {
  User2, Image as ImageIcon, TreeDeciduous, QrCode, Mail, Lock, Sparkles,
  Settings as SettingsIcon, ShieldCheck, Package, ArrowLeft, ExternalLink, Eye, BookOpen,
  CalendarDays, Mic,
} from 'lucide-react';

import { Overview } from './memorial-admin/Overview';
import { ProfileTab } from './memorial-admin/ProfileTab';
import { MediaTab } from './memorial-admin/MediaTab';
import { VideoTab } from './memorial-admin/VideoTab';
import { FamilyTab } from './memorial-admin/FamilyTab';
import { QRTab } from './memorial-admin/QRTab';
import { AccessTab } from './memorial-admin/AccessTab';
import { VaultTab } from './memorial-admin/VaultTab';
import { AITab } from './memorial-admin/AITab';
import { AdminsTab } from './memorial-admin/AdminsTab';
import { BillingTab } from './memorial-admin/BillingTab';
import { SettingsTab } from './memorial-admin/SettingsTab';
import { MemoryBookTab } from './memorial-admin/MemoryBookTab';
import { ServicesTab } from './memorial-admin/ServicesTab';
import { VoiceTab } from './memorial-admin/VoiceTab';
import { OrdersTab } from './memorial-admin/OrdersTab';

const SECTIONS = [
  { id: 'overview', icon: Eye, label: 'Overview' },
  { id: 'profile', icon: User2, label: 'Profile' },
  { id: 'services', icon: CalendarDays, label: 'Services' },
  { id: 'media', icon: ImageIcon, label: 'Memories' },
  { id: 'voice', icon: Mic, label: 'Voice Memories' },
  { id: 'video', icon: Sparkles, label: 'Life Journey' },
  { id: 'book', icon: BookOpen, label: 'Memory Book' },
  { id: 'family', icon: TreeDeciduous, label: 'Family' },
  { id: 'qr', icon: QrCode, label: 'QR Code' },
  { id: 'orders', icon: Package, label: 'Plaque Orders' },
  { id: 'access', icon: Mail, label: 'Access Requests' },
  { id: 'vault', icon: Lock, label: 'Family Vault' },
  { id: 'ai', icon: Sparkles, label: 'AI Memories' },
  { id: 'admins', icon: ShieldCheck, label: 'Admins' },
  { id: 'billing', icon: Package, label: 'Billing' },
  { id: 'settings', icon: SettingsIcon, label: 'Settings' },
];

export default function MemorialAdmin() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') || 'overview').toLowerCase();
  const [active, setActive] = useState(
    SECTIONS.some((s) => s.id === initialTab) ? initialTab : 'overview',
  );
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);

  const refresh = useCallback(() => {
    api.get(`/memorials/${id}/summary`)
      .then(({ data }) => setSummary(data))
      .catch((e) => setErr(formatApiError(e)));
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  // iter95.3 — wizard / Stripe redirect toasts. Show once, then clean the
  // query string so reloads don't re-trigger them.
  useEffect(() => {
    const draftSaved = searchParams.get('draft_saved');
    const activated = searchParams.get('activated');
    if (draftSaved) {
      toast.success('Your memorial draft is saved. Choose a plan when you\u2019re ready to activate.');
    } else if (activated) {
      toast.success('Memorial activated — QR, sharing, and public preview are unlocked.');
    }
    if (draftSaved || activated) {
      const sp = new URLSearchParams(searchParams);
      sp.delete('draft_saved');
      sp.delete('activated');
      const qs = sp.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? '?' + qs : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <div className="ml-container py-20 text-center">
          <h2 className="font-serif text-3xl text-[hsl(var(--primary))]">{err}</h2>
          <Link to="/dashboard" className="ml-btn-outline mt-6 inline-flex">Back to dashboard</Link>
        </div>
        <Footer />
      </div>
    );
  }
  if (!summary) return <div className="min-h-screen bg-background flex items-center justify-center font-serif text-xl text-[hsl(var(--primary))]">Loading…</div>;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {summary.viewer_is_support_admin && (
        <div className="bg-[#7A1F1F] text-white px-4 py-2.5 text-center text-xs sm:text-sm" data-testid="admin-support-banner">
          <span className="font-semibold tracking-[0.14em] uppercase">Admin Support Mode</span>
          <span className="mx-2 opacity-60">—</span>
          This account is being viewed by a MyLeaf Legacy administrator. Every action is audit-logged.
          <span className="block sm:inline sm:ml-3 mt-1 sm:mt-0">
            <Link to={`/admin/accounts/${summary.memorial.owner_id}`} className="underline font-medium" data-testid="support-banner-account-link">Account</Link>
            <span className="mx-1.5 opacity-60">|</span>
            <Link to={`/admin/memorials/${id}`} className="underline font-medium" data-testid="support-banner-memorial-link">Memorial dossier</Link>
            <span className="mx-1.5 opacity-60">|</span>
            <Link to={`/admin/plaque-orders`} className="underline font-medium" data-testid="support-banner-orders-link">Acrylic orders</Link>
          </span>
        </div>
      )}
      <Navbar />
      <div className="ml-container py-8 flex-1">
        <div className="flex flex-wrap justify-between items-start gap-4">
          <div>
            <Link to="/dashboard" className="text-xs font-semibold tracking-widest uppercase text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1.5" data-testid="back-to-dashboard">
              <ArrowLeft className="w-3.5 h-3.5" /> All memorials
            </Link>
            <h1 className="font-serif text-4xl sm:text-5xl text-[hsl(var(--primary))] mt-3">{summary.memorial.full_name}</h1>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
              {summary.memorial.date_of_birth || '—'} — {summary.memorial.date_of_passing || '—'} ·
              <span className="capitalize ml-1">{summary.plan.name} plan</span>
            </div>
          </div>
          <Link to={`/m/${summary.memorial.slug}`} target="_blank" rel="noreferrer" className="ml-btn-outline" data-testid="view-public-btn">
            <ExternalLink className="w-4 h-4" /> View public page
          </Link>
        </div>

        <div className="grid lg:grid-cols-[240px_minmax(0,1fr)] gap-8 mt-10">
          {/* Sidebar */}
          <aside className="lg:sticky top-24 self-start">
            <div className="ml-card !p-3">
              {SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  data-testid={`tab-${s.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition text-left ${active === s.id ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]' : 'hover:bg-[hsl(var(--muted))]'}`}
                >
                  <s.icon className="w-4 h-4" strokeWidth={1.5} /> {s.label}
                </button>
              ))}
            </div>
          </aside>

          <section className="min-w-0">
            {active === 'overview' && <Overview summary={summary} />}
            {active === 'profile' && <ProfileTab memorial={summary.memorial} onChange={refresh} />}
            {active === 'services' && <ServicesTab memorialId={id} />}
            {active === 'media' && <MediaTab memorialId={id} summary={summary} />}
            {active === 'voice' && <VoiceTab memorialId={id} />}
            {active === 'video' && <VideoTab memorialId={id} onOpenBook={() => setActive('book')} />}
            {active === 'book' && <MemoryBookTab memorial={summary.memorial} memorialId={id} onChange={refresh} />}
            {active === 'family' && <FamilyTab memorialId={id} memorial={summary.memorial} />}
            {active === 'qr' && <QRTab memorialId={id} memorial={summary.memorial} />}
            {active === 'orders' && <OrdersTab memorialId={id} />}
            {active === 'access' && <AccessTab memorialId={id} onChange={refresh} />}
            {active === 'vault' && <VaultTab memorialId={id} />}
            {active === 'ai' && <AITab memorial={summary.memorial} memorialId={id} isOwner={summary.is_owner} onChange={refresh} />}
            {active === 'admins' && <AdminsTab memorialId={id} isOwner={summary.is_owner} summary={summary} />}
            {active === 'billing' && <BillingTab summary={summary} memorialId={id} />}
            {active === 'settings' && <SettingsTab memorialId={id} memorial={summary.memorial} onChange={refresh} />}
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
